import { Injectable } from '@angular/core';
import { AES, Utf8 } from 'crypto-es';

@Injectable({
  providedIn: 'root',
})
export class LocalService {
  private key = 'worldcup2026!~qaZ<';
  private keyPromise: Promise<CryptoKey> | null = null;
  private readonly v2Prefix = 'wc26v2:';

  constructor() {}

  public saveData(key: string, value: any): void {
    localStorage.setItem(key, this.encrypt(value));
  }

  public async saveDataV2(key: string, value: any): Promise<void> {
    if (!this.hasWebCrypto()) {
      this.saveData(key, value);
      return;
    }

    localStorage.setItem(key, await this.encryptV2(value));
  }

  public getData(key: string): any {
    const data = localStorage.getItem(key) ?? '';
    return this.decrypt(data);
  }

  public async getDataV2<T = any>(key: string): Promise<T | null> {
    const data = localStorage.getItem(key) ?? '';

    if (!data) {
      return null;
    }

    if (data.startsWith(this.v2Prefix) && this.hasWebCrypto()) {
      try {
        return await this.decryptV2<T>(data);
      } catch {
        return null;
      }
    }

    // Legacy fallback plus in-place migration to v2 when Web Crypto is available.
    const legacy = this.decrypt(data);
    if (legacy !== null && this.hasWebCrypto()) {
      await this.saveDataV2(key, legacy);
    }
    return legacy;
  }

  public removeData(key: string): void {
    localStorage.removeItem(key);
  }

  public clearData(): void {
    localStorage.clear();
  }

  private encrypt(value: any): string {
    return AES.encrypt(JSON.stringify(value), this.key).toString();
  }

  private decrypt(txtToDecrypt: string): any {
    if (!txtToDecrypt) {
      return null;
    }

    const decrypted = AES.decrypt(txtToDecrypt, this.key).toString(Utf8);

    if (!decrypted) {
      return null;
    }

    return JSON.parse(decrypted);
  }

  private hasWebCrypto(): boolean {
    return (
      typeof window !== 'undefined' && !!window.crypto && !!window.crypto.subtle
    );
  }

  private async encryptV2(value: any): Promise<string> {
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const encoded = new TextEncoder().encode(JSON.stringify(value));
    const key = await this.getOrCreateAesKey();
    const encrypted = await window.crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv as unknown as BufferSource },
      key,
      encoded,
    );

    const ivB64 = this.bytesToBase64(iv);
    const dataB64 = this.bytesToBase64(new Uint8Array(encrypted));
    return `${this.v2Prefix}${ivB64}.${dataB64}`;
  }

  private async decryptV2<T = any>(payload: string): Promise<T | null> {
    const content = payload.replace(this.v2Prefix, '');
    const [ivB64, dataB64] = content.split('.');
    if (!ivB64 || !dataB64) {
      return null;
    }

    const iv = this.base64ToBytes(ivB64);
    const encrypted = this.base64ToBytes(dataB64);
    const key = await this.getOrCreateAesKey();

    const decrypted = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: iv as unknown as BufferSource },
      key,
      encrypted as unknown as BufferSource,
    );

    return JSON.parse(new TextDecoder().decode(decrypted)) as T;
  }

  private getOrCreateAesKey(): Promise<CryptoKey> {
    if (!this.keyPromise) {
      this.keyPromise = this.deriveAesKey();
    }
    return this.keyPromise;
  }

  private async deriveAesKey(): Promise<CryptoKey> {
    const raw = new TextEncoder().encode(this.key);
    const hash = await window.crypto.subtle.digest('SHA-256', raw);
    return window.crypto.subtle.importKey(
      'raw',
      hash,
      { name: 'AES-GCM' },
      false,
      ['encrypt', 'decrypt'],
    );
  }

  private bytesToBase64(bytes: Uint8Array): string {
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  private base64ToBytes(base64: string): Uint8Array {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }
}
